import AuthService from './auth/AuthService';
import ProjectService from './projects/ProjectService';
import OrganizationService from './organizations/OrganizationService';
import ProfileService from './profile/ProfileService';
import cookies from './auth/cookies';

import { setupDefaultsAxiosInstance } from './axiosInstance';

import createUUID from './createUUID';

/**
 * This class is the core obeject for the Pigoneline platform.
 */

class Pigeonline {
  /**
   * Instantiates the PigeonLine class
   * @param {object} options
   * Possible keys:
   * - baseUrl: The location of the app backend that the frontend should make requests to.
   */
  constructor(options, app) {
    // Check correct setup options have been passed
    if (typeof options !== 'object' && options !== null) {
      throw Error('Pigeonline: required options object not passed to constructor');
    }

    const requiredOptions = ['baseUrl'];

    const missingOptions = requiredOptions.filter((opt) => {
      return !(opt in options);
    });

    if (missingOptions.length) {
      throw Error('Pigeonline: Missing required options: ' + missingOptions);
    }

    // Initialize
    this.options = options;

    this.authService = new AuthService(options);
    this.projects = new ProjectService({
      ...options,
      core: this,
    });

    this.organizations = new OrganizationService({
      ...options,
      core: this,
    });

    this.profile = new ProfileService({
      ...options,
      core: this,
    });

    // Initialize plugins
    if ('plugins' in options) {
      options.plugins.forEach((Plugin) => {
        this[Plugin.name().replace(/./, (w) => w.toLowerCase())] = new Plugin({
          ...options,
          core: this,
          app,
        });
      });
    }

    // Setup axios defaults
    setupDefaultsAxiosInstance({
      ...options,
      core: this,
    });
  }

  /**
   * Registers a new user
   * @param {string} email
   * @param {string} password
   */
  async register(
    email,
    password,
    inviteId,
    personalExplanation,
    personalDatasetType,
    personalOrganization,
    role,
    referral,
  ) {
    let skipEmailVerification = false;
    if (inviteId) {
      skipEmailVerification = true;
    }
    let explanation = '';
    if (personalExplanation) {
      explanation = personalExplanation;
    }

    let datasetType = '';
    if (personalDatasetType) {
      datasetType = personalDatasetType;
    }

    let organization = '';
    if (personalOrganization) {
      organization = personalOrganization;
    }

    const user = await this.authService.register(
      email,
      password,
      skipEmailVerification,
      explanation,
      datasetType,
      organization,
      role,
      referral,
    );
    cookies.setCookie('user', JSON.stringify({ email: user.email }));
    return user;
  }

  /**
   * Attempts to authenticate a user and if successful stores the authentication token
   * @param {string} email
   * @param {string} password
   */
  async login(email, password) {
    // eslint-disable-next-line no-unused-vars
    const { access, refresh, expires_in } = await this.authService.login(email, password);
    this.setAuthToken(access, expires_in.access / (24 * 60 * 60));

    // set refresh token expires_in
    var d = new Date();
    d.setTime(d.getTime() + expires_in.refresh * 1000);
    cookies.setCookie('refresh_token_expires', d);

    // save user email (for future login)
    cookies.setCookie('email', email);

    return access;
  }

  /**
   * Attempts to refresh the user token
   */
  async refresh() {
    const { access, expires_in } = await this.authService.refresh();
    this.setAuthToken(access, expires_in.access / (24 * 60 * 60));

    return access;
  }

  /**
   * Log the user our from the session by deleting all cookies
   */
  async logout() {
    cookies.deleteCookie('access_token');
    cookies.deleteCookie('access_token_expires');
    cookies.deleteCookie('refresh_token_expires');
    cookies.deleteCookie('email');
  }

  /**
   * Sets the authentication token to be used for making requests that require user auth
   * @param {string} accessToken
   * @param {number} expiration time in days
   */
  setAuthToken(accessToken, expiration) {
    cookies.setCookie('access_token', accessToken);
    var d = new Date();
    d.setTime(d.getTime() + expiration * 24 * 60 * 60 * 1000);
    cookies.setCookie('access_token_expires', d);
  }

  /**
   * Retrieves the stored auth token
   * @return {string} Auth token
   */
  getAuthToken() {
    return cookies.getCookie('access_token');
  }

  /**
   * Retrieves the correct value for the Authorization header to enable making requests to
   * a protected URL
   * @return {string} Auth header value
   */
  getAuthHeader() {
    return 'Token ' + this.getAuthToken();
  }

  getUser() {
    const jwt = cookies.getCookie('access_token');

    if (!jwt) {
      return null;
    } else {
      return parseJwt(cookies.getCookie('access_token'));
    }
  }

  getUserEmail() {
    return cookies.getCookie('email');
  }

  /**
   * Determins if a currently authenticated use is present
   * @returns {boolean}
   */
  userIsAuthenticated() {
    return this.getAuthToken() !== '';
  }

  createUUID() {
    return createUUID();
  }
}

function parseJwt(token) {
  var base64Url = token.split('.')[1];
  var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  var jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );

  return JSON.parse(jsonPayload);
}

export default Pigeonline;
